import React from 'react';
import _ from 'lodash';

/**
 * A higher order component that passes a scope prop to the wrapped
 * component based on well-named prefetch data keys, configurable via the
 * {@code nodeScopeMapping} and {@code userScopeMapping} props. The scope
 * contains information about the node and user when available in the prefetch, as
 * well as builds up a canonical text key path. Possible text keys paths:
 *
 * <ul>
 *   <li>title.node.top</li>
 *   <li>title.node.leaf</li>
 *   <li>title.node.top.user.my</li>
 *   <li>title.node.leaf.user.them</li>
 *   <li>title.user.my</li>
 *   <li>title.user.them</li>
 * </ul>
 *
 * @author Adam Ayres
 *
 * @param WrappedComponent the component to wrap
 * @param baseTextKey the base text key path prefix
 * @param nodeScopeMapping the prefetch key to look for a node scope
 * @param userScopeMapping the prefetch key to look for a user scope
 * @returns {function(*): *} the wrapped component
 */
const withScope = (
  WrappedComponent,
  baseTextKey = 'title',
  nodeScopeMapping = 'nodeScope',
  userScopeMapping = 'userScope'
) => {
  const ComponentWithScope = props => {
    const { globals, prefetch } = props;
    const nodeScope = _.get(prefetch.getData(nodeScopeMapping), '[0]', null);
    const userScope = _.get(prefetch.getData(userScopeMapping), '[0]', null);

    const scope = {};
    scope.nodeTextKey = `${baseTextKey}.node`;
    scope.userTextKey = `${baseTextKey}.user`;

    if (nodeScope !== null) {
      scope.node = nodeScope;

      if (_.get(nodeScope, 'id', '').split(':')[0] === 'community') {
        scope.nodeTextKey += '.top';
      } else {
        scope.nodeTextKey += '.leaf';
      }
    } else {
      scope.nodeTextKey += 'default';
    }

    if (userScope !== null) {
      scope.user = userScope;

      if (userScope.id === _.get(globals, 'userId')) {
        scope.userTextKey += '.my';
      } else {
        scope.userTextKey += '.them';
      }
    } else {
      scope.userTextKey = 'default';
    }

    return <WrappedComponent {...props} scope={scope} />;
  };

  ComponentWithScope.displayName = 'withScope';
  return ComponentWithScope;
};

export default withScope;
